import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Blocking acknowledge modal for broadcast notifications.
 *
 * Props:
 *   notifications: Array<{ id, scope, club_name, order_name, message }> — list,
 *                  rendered stacked. Pass null/empty to render nothing.
 *   notification:  Legacy single-object form. If passed, it's wrapped in a
 *                  one-element array. Kept for older call sites.
 *   onAcknowledge: () => void
 *   compact:       boolean — narrower variant for the iPhone-targeted Picking
 *                  Portal.
 *
 * When rendered, blocks page interaction via a fixed-position backdrop. The
 * only path to dismissal is the Acknowledge button — backdrop click and Esc
 * key are intentionally ignored (broadcast model: operator MUST see it).
 */
export default function NotificationBroadcastModal({
    notifications,
    notification,
    onAcknowledge,
    compact = false,
}) {
    // Normalize to a list. Order-specific entries appear before club entries
    // (server already returns them in that order).
    const list = Array.isArray(notifications) && notifications.length > 0
        ? notifications
        : (notification ? [notification] : []);

    const headersRef = useRef([]);

    // Lock background scroll while the modal is up.
    useEffect(() => {
        if (list.length === 0) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [list.length]);

    // Shrink-to-fit each header line so long club names / order names always
    // fit on one line. Re-runs on resize.
    const measureAndFit = useCallback(() => {
        for (const el of headersRef.current) {
            if (!el) continue;
            const parent = el.parentElement;
            if (!parent || !parent.clientWidth) continue;
            el.style.fontSize = '';
            void el.offsetWidth;
            let size = parseFloat(window.getComputedStyle(el).fontSize) || 24;
            const minSize = 10;
            const target = parent.clientWidth - 4;
            for (let i = 0; i < 60 && size > minSize && el.scrollWidth > target; i++) {
                size -= 1;
                el.style.fontSize = `${size}px`;
            }
        }
    }, []);

    useEffect(() => {
        if (list.length === 0) return undefined;
        const raf = requestAnimationFrame(measureAndFit);
        window.addEventListener('resize', measureAndFit);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', measureAndFit);
        };
    }, [list, compact, measureAndFit]);

    if (list.length === 0) return null;

    const setHeaderRef = (i) => (el) => {
        headersRef.current[i] = el;
    };

    return (
        <div
            className={`notif-broadcast__backdrop${compact ? ' notif-broadcast__backdrop--compact' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notif-broadcast-title"
        >
            <div
                className={`notif-broadcast__box${compact ? ' notif-broadcast__box--compact' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="notif-broadcast__bolts notif-broadcast__bolts--top">
                    ⚡ ⚡ ⚡ ⚡ ⚡
                </div>

                {list.map((n, i) => {
                    const isJumbled = n.scope === 'jumbled_bundle' && n._jumbled;
                    const isOrder = n.scope === 'order' || !!n.order_name;
                    const headerText = isOrder
                        ? `ORDER ${(n.order_name || '').toUpperCase()}`
                        : (n.club_name || '').toUpperCase();
                    return (
                        <React.Fragment key={n.id ?? i}>
                            {i > 0 && <div className="notif-broadcast__sep" />}
                            <div className="notif-broadcast__title" id={i === 0 ? 'notif-broadcast-title' : undefined}>
                                {isJumbled ? '⚠ JUMBLED BUNDLE ALERT' : 'IMPORTANT NOTIFICATION FOR'}
                            </div>
                            <div className="notif-broadcast__club" ref={setHeaderRef(i)}>
                                {headerText}
                            </div>
                            {isJumbled ? (
                                <JumbledBundleBody jumbled={n._jumbled} />
                            ) : (
                                <div className="notif-broadcast__message">
                                    {n.message}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}

                <button
                    type="button"
                    className="notif-broadcast__ack"
                    onClick={onAcknowledge}
                    autoFocus
                >
                    Acknowledge
                </button>
                <div className="notif-broadcast__bolts notif-broadcast__bolts--bottom">
                    ⚡ ⚡ ⚡ ⚡ ⚡
                </div>
            </div>
        </div>
    );
}

/**
 * Rich body for a `scope='jumbled_bundle'` synthetic notification.
 * Format (matches user spec 2026-07-16):
 *
 *   Bundle:                     ← bold
 *   "<label>" <player_suffix>   ← plain, left
 *
 *   Loc A: N items              ← plain, left
 *   Loc B: M items              ← plain, left
 *
 *   Loc A                       ← bold, centered
 *     1× item                   ← left
 *     …
 *   Loc B                       ← bold, centered
 *     …
 */
function JumbledBundleBody({ jumbled }) {
    if (!jumbled) return null;
    const fos = Array.isArray(jumbled.fos) ? jumbled.fos : [];
    const bundleLine = `"${jumbled.label || ''}"${jumbled.player_suffix ? ` ${jumbled.player_suffix}` : ''}`;

    return (
        <div className="notif-broadcast__message" style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 700 }}>Bundle:</span>
            </div>
            <div style={{ marginBottom: 12 }}>{bundleLine}</div>

            <div style={{ marginBottom: 12 }}>
                {fos.map((f, i) => {
                    const qty = (f.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
                    return (
                        <div key={i}>
                            {f.location_name || '(unknown)'}: {qty} {qty === 1 ? 'item' : 'items'}
                        </div>
                    );
                })}
            </div>

            {fos.map((f, i) => (
                <div key={`fo-${i}`} style={{ marginBottom: 10 }}>
                    <div style={{
                        fontWeight: 700,
                        textAlign: 'center',
                        marginBottom: 4,
                    }}>
                        {f.location_name || '(unknown)'}
                    </div>
                    {(f.items || []).map((it, j) => (
                        <div key={j} style={{ textAlign: 'left' }}>
                            {it.quantity}× {it.title}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
